import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Grid, Button, TextField, Select, MenuItem,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Alert, IconButton, CircularProgress, FormControl, InputLabel, Divider,
    Tooltip
} from '@mui/material';
import {
    Save as SaveIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Calculate as CalculateIcon
} from '@mui/icons-material';
import { getConfig, saveConfig } from '../api/api';

const UnidadesProducao = () => {
    const [unidades, setUnidades] = useState([]);
    const [centrosDisponiveis, setCentrosDisponiveis] = useState([]);
    const [loading, setLoading] = useState(true);
    const [feedback, setFeedback] = useState({ message: '', type: '' });
    const [novaUnidade, setNovaUnidade] = useState({ centro: '', unidade: '' });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const centrosRes = await getConfig('centros_custo');
                const centrosAtivos = Array.isArray(centrosRes.data)
                    ? centrosRes.data.map(c => c.nome)
                    : [];
                setCentrosDisponiveis(centrosAtivos);

                const unidadesRes = await getConfig('unidades_producao');
                setUnidades(Array.isArray(unidadesRes.data) ? unidadesRes.data : []);
            } catch (error) {
                console.error("Erro ao carregar dados:", error);
                setFeedback({ message: 'Erro ao carregar dados de configuração.', type: 'error' });
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleSave = async () => {
        setLoading(true);
        try {
            await saveConfig('unidades_producao', unidades);
            setFeedback({ message: 'Configuração salva com sucesso!', type: 'success' });
        } catch (error) {
            console.error("Erro ao salvar:", error);
            setFeedback({ message: 'Erro ao salvar a configuração.', type: 'error' });
        } finally {
            setLoading(false);
            setTimeout(() => setFeedback({ message: '', type: '' }), 3000);
        }
    };

    const handleAddUnit = () => {
        if (!novaUnidade.centro || !novaUnidade.unidade) {
            setFeedback({ message: 'Centro de Custo e Unidade são obrigatórios.', type: 'error' });
            return;
        }

        if (unidades.some(u => u.centro === novaUnidade.centro)) {
            setFeedback({ message: `${novaUnidade.centro} já possui uma unidade definida.`, type: 'warning' });
            return;
        }

        setUnidades([...unidades, { centro: novaUnidade.centro, unidade: novaUnidade.unidade }]);
        setNovaUnidade({ centro: '', unidade: '' });
        setFeedback({ message: 'Nova unidade adicionada. Salve para persistir.', type: 'success' });
        setTimeout(() => setFeedback({ message: '', type: '' }), 3000);
    };

    const handleRemoveUnit = (centro) => {
        setUnidades(unidades.filter(u => u.centro !== centro));
        setFeedback({ message: `Unidade removida para ${centro}.`, type: 'warning' });
        setTimeout(() => setFeedback({ message: '', type: '' }), 3000);
    };

    const availableCentros = centrosDisponiveis.filter(cc => !unidades.some(u => u.centro === cc));

    if (loading && !unidades.length && !centrosDisponiveis.length) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <CircularProgress />
        </Box>
    );

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', display: 'flex', alignItems: 'center' }}>
                <CalculateIcon sx={{ mr: 2, color: 'primary.main' }} /> Gerenciamento de Unidades de Produção
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                Defina a Unidade de Medida para cada Centro de Custo produtivo.
            </Typography>

            {feedback.message && (
                <Alert severity={feedback.type} sx={{ mb: 3 }}>
                    {feedback.message}
                </Alert>
            )}

            <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>Adicionar Nova Unidade</Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={5}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Centro de Custo</InputLabel>
                            <Select
                                value={novaUnidade.centro}
                                label="Centro de Custo"
                                onChange={(e) => setNovaUnidade({ ...novaUnidade, centro: e.target.value })}
                            >
                                <MenuItem value=""><em>Selecione...</em></MenuItem>
                                {availableCentros.map(centro => (
                                    <MenuItem key={centro} value={centro}>{centro}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Unidade de Medida"
                            value={novaUnidade.unidade}
                            onChange={(e) => setNovaUnidade({ ...novaUnidade, unidade: e.target.value })}
                            placeholder="Ex: Horas de UTI"
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            onClick={handleAddUnit}
                            disabled={!novaUnidade.centro || !novaUnidade.unidade}
                            startIcon={<AddIcon />}
                        >
                            Adicionar
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                    Unidades Configuradas ({unidades.length})
                </Typography>

                {unidades.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        Nenhuma unidade configurada.
                    </Typography>
                ) : (
                    <TableContainer sx={{ maxHeight: 500, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Centro de Custo</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Unidade de Medida</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {unidades.map((unit) => (
                                    <TableRow key={unit.centro} hover>
                                        <TableCell>{unit.centro}</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary' }}>{unit.unidade}</TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="Remover Unidade">
                                                <IconButton onClick={() => handleRemoveUnit(unit.centro)} color="error" size="small">
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {unidades.length > 0 && (
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            color="success"
                            onClick={handleSave}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                            size="large"
                        >
                            {loading ? "Salvando..." : `Salvar Configuração (${unidades.length})`}
                        </Button>
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default UnidadesProducao;
